import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import { RecruitmentDrawer } from '../recruitment/RecruitmentDrawer';
import { RecruitmentPipelineFunnel } from '../recruitment/RecruitmentPipelineFunnel';
import { RecruitmentFilters, type FilterValues } from '../recruitment/RecruitmentFilters';
import { RecruitmentTable } from '../recruitment/RecruitmentTable';
import { TerminationDialog } from '../recruitment/TerminationDialog';
import { LwThemeScope, LwPageHeader, LwButton, LwEmpty } from '@/components/lw';
import { useRecruits, useRecruitment } from '@/hooks/useRecruitment';
import { useAuth } from '@/contexts/useAuth';
import { lookupNmls } from '@/services/loProspectService';
import { getErrorMessage } from '@/utils/errorMessage';
import type { RecruitmentFormValues } from '../recruitment/validations';
import type { RecruitmentSaveOutcome } from '../recruitment/formDefaults';
import type { RecruitmentCandidate } from '@/types/recruitment';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('Recruiting', 'recruitment-dashboard');

export const Recruiting: React.FC = () => {
  // Database integration - fetch recruits from Supabase
  const { recruits, loading: recruitsLoading, error: recruitsError, refetch } = useRecruits();
  const { createRecruit, updateRecruit, deleteRecruit } = useRecruitment();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<RecruitmentCandidate | null>(null);
  const [initialDrawerTab, setInitialDrawerTab] = useState<string | undefined>(undefined);
  const [autoOpenReviewModal, setAutoOpenReviewModal] = useState(false);
  const [terminationDialogOpen, setTerminationDialogOpen] = useState(false);
  const [candidateToTerminate, setCandidateToTerminate] = useState<RecruitmentCandidate | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Tracks the last candidate ID applied via deep link so we don't repeat
  // it on incidental re-renders, but allow re-clicks of the same email
  // after the drawer closes (handleDrawerOpenChange clears this ref).
  const consumedCandidateRef = useRef<string | null>(null);
  const [filters, setFilters] = useState<FilterValues>({
    searchTerm: '',
    status: 'all',
    experience: 'all',
    dateRange: 'all'
  });

  // Deep-link from notification emails: ?candidate={id}&tab=contract-details&action=review-contract
  // Apply once when recruits finish loading, then strip the params so refreshes
  // / navigation don't re-fire. InternalPage.tsx has already normalized legacy
  // aliases (e.g. tab=contract → contract-details) before we read here.
  useEffect(() => {
    if (recruitsLoading) {
      return;
    }

    const candidateId = searchParams.get('candidate');
    if (!candidateId) {
      return;
    }
    if (consumedCandidateRef.current === candidateId) {
      return;
    }

    const stripDeepLinkParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('candidate');
      next.delete('tab');
      next.delete('action');
      setSearchParams(next, { replace: true });
    };

    const match = recruits.find(r => r.id === candidateId);
    if (!match) {
      toast.error('Unable to open that candidate (not found or access denied).');
      consumedCandidateRef.current = candidateId;
      stripDeepLinkParams();
      return;
    }

    setSelectedCandidate(match);
    setInitialDrawerTab(searchParams.get('tab') ?? undefined);
    setAutoOpenReviewModal(searchParams.get('action') === 'review-contract');
    setDrawerOpen(true);
    consumedCandidateRef.current = candidateId;
    stripDeepLinkParams();
  }, [recruits, recruitsLoading, searchParams, setSearchParams]);

  // Centralized drawer close handler — clears deep-link state so a subsequent
  // manual Add/Edit doesn't inherit Contract Details / auto-modal, and clears
  // the consumed-candidate ref so re-clicking the same email reopens cleanly.
  const handleDrawerOpenChange = (next: boolean) => {
    setDrawerOpen(next);
    if (!next) {
      setInitialDrawerTab(undefined);
      setAutoOpenReviewModal(false);
      consumedCandidateRef.current = null;
    }
  };

  // Auth state monitoring
  const { user } = useAuth();
  React.useEffect(() => {
    log.debug('Auth state check', {
      hasUser: !!user,
      userId: user?.id,
      organizationId: (user as { organization_id?: unknown } | null)?.organization_id,
      email: user?.email
    });
  }, [user]);

  // Filter candidates based on current filters
  const filteredCandidates = useMemo(() => {
    return recruits.filter((candidate) => {
      // Search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch =
          candidate.firstName.toLowerCase().includes(searchLower) ||
          candidate.lastName.toLowerCase().includes(searchLower) ||
          candidate.email.toLowerCase().includes(searchLower) ||
          candidate.phone.includes(filters.searchTerm) ||
          candidate.nmlsNumber.includes(filters.searchTerm);

        if (!matchesSearch) {
return false;
}
      }

      // Status filter
      if (filters.status !== 'all' && candidate.status !== filters.status) {
        return false;
      }

      // Experience filter
      if (filters.experience !== 'all') {
        const exp = candidate.yearsOfExperience;
        // Skip filtering if experience not provided
        if (exp !== null) {
          switch (filters.experience) {
            case '0-3':
              if (exp > 3) {
return false;
}
              break;
            case '3-7':
              if (exp <= 3 || exp > 7) {
return false;
}
              break;
            case '7-12':
              if (exp <= 7 || exp > 12) {
return false;
}
              break;
            case '12+':
              if (exp <= 12) {
return false;
}
              break;
          }
        }
      }

      // Date range filter
      if (filters.dateRange !== 'all') {
        const days = parseInt(filters.dateRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        const candidateDate = new Date(candidate.createdAt);

        if (candidateDate < cutoffDate) {
return false;
}
      }

      return true;
    });
  }, [recruits, filters]);

  // Handlers
  const handleAddLoanOfficer = () => {
    setSelectedCandidate(null);
    setDrawerOpen(true);
  };

  const handleEditCandidate = (candidate: RecruitmentCandidate) => {
    setSelectedCandidate(candidate);
    setDrawerOpen(true);
  };

  const _handleDeleteCandidate = async (candidateId: string) => {
    try {
      await deleteRecruit(candidateId);
      // Refetch to update list
      await refetch();
    } catch (error) {
      log.error('Failed to delete recruit', { candidateId }, error as Error);
      // eslint-disable-next-line no-alert
      alert('Failed to delete recruit. Please try again.');
    }
  };

  const handleTerminateCandidate = (candidate: RecruitmentCandidate) => {
    setCandidateToTerminate(candidate);
    setTerminationDialogOpen(true);
  };

  const handleTerminationComplete = async () => {
    if (candidateToTerminate) {
      try {
        // Update recruit status to Terminated
        await updateRecruit(candidateToTerminate.id, { status: 'Terminated' });
        // Refetch to update list
        await refetch();
        log.info('Recruit terminated successfully', { recruitId: candidateToTerminate.id });
      } catch (error) {
        log.error('Failed to update recruit status after termination', {
          recruitId: candidateToTerminate.id
        }, error as Error);
      }
    }
    setCandidateToTerminate(null);
  };

  const handleSaveCandidate = async (data: RecruitmentFormValues): Promise<RecruitmentSaveOutcome> => {
    log.info('Save handler called', {
      mode: selectedCandidate ? 'update' : 'create',
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email
    });

    try {
      if (selectedCandidate) {
        // Update existing candidate
        log.info('Updating recruit', { recruitId: selectedCandidate.id });
        const updatedCandidate = await updateRecruit(selectedCandidate.id, data);

        // updateRecruit returns null (without throwing) when the auth context
        // has no user id. Treating that as success is what left the drawer
        // permanently dirty while showing "updated successfully".
        if (!updatedCandidate) {
          log.error('Update returned no record', { metadata: { recruitId: selectedCandidate.id } });
          return {
            status: 'failed',
            message: 'The update did not complete. Please reload and try again.'
          };
        }

        log.info('Update successful', { recruitId: selectedCandidate.id });
        setSelectedCandidate(updatedCandidate);
        await refetch();
        log.info('Save complete');
        return { status: 'saved', candidate: updatedCandidate };
      } else {
        // Cross-pipeline guard: an active Talent Pipeline prospect must come in
        // through "Transfer to recruiting" on their prospect record (keeps the
        // link + comp fields). Best-effort preflight for a clear message — the
        // recruits DB trigger is the authoritative backstop.
        if (user?.organization_id) {
          try {
            const match = await lookupNmls(user.organization_id, data.nmlsNumber);
            if (match?.source === 'lo_prospect' && match.stage !== 'passed' && match.stage !== 'in_recruiting') {
              // Blocked, not failed, and definitely not saved. Reported as an
              // outcome so the drawer keeps the form dirty and shows no
              // success — it previously returned silently after an alert while
              // the drawer went on to announce a successful save.
              return {
                status: 'blocked',
                message: `NMLS ${data.nmlsNumber} is an active prospect in the Talent Pipeline — use "Transfer to recruiting" on their prospect record instead.`
              };
            }
          } catch {
            // advisory only; the DB trigger enforces the guard
          }
        }

        // Create new candidate
        log.info('Creating new recruit');
        const result = await createRecruit(data);

        if (!result) {
          log.error('Create returned no record', { metadata: { email: data.email } });
          return {
            status: 'failed',
            message: 'The candidate could not be created. Please reload and try again.'
          };
        }

        log.info('Create successful', { recruitId: result.id });
        setSelectedCandidate(result);
        await refetch();
        log.info('Save complete');
        return { status: 'saved', candidate: result };
      }
    } catch (error) {
      log.error('Failed to save recruit', {
        mode: selectedCandidate ? 'update' : 'create',
        email: data.email
      }, error as Error);
      return { status: 'failed', message: getErrorMessage(error) };
    }
  };

  const handleExport = () => {
    // TODO: Implement CSV export
    log.info('Export requested', { candidateCount: filteredCandidates.length });
    // eslint-disable-next-line no-alert
    alert('Export functionality coming soon!');
  };

  // Show loading state
  if (recruitsLoading) {
    return (
      <LwThemeScope className="lw-ds-page">
        <div className="wrap">
          <LwEmpty>Loading recruits…</LwEmpty>
        </div>
      </LwThemeScope>
    );
  }

  // Show error state
  if (recruitsError) {
    return (
      <LwThemeScope className="lw-ds-page">
        <div className="wrap">
          <div className="err-box">
            <div>
              <div className="err-title">Error loading recruits</div>
              <div className="err-detail">{recruitsError}</div>
              <button className="err-link" onClick={() => refetch()} type="button">
                Retry
              </button>
            </div>
          </div>
        </div>
      </LwThemeScope>
    );
  }

  return (
    <LwThemeScope className="lw-ds-page">
      <div className="wrap">
        <LwPageHeader
          eyebrow="Talent Pipeline"
          title={
            <>
              Recruiting <em>Pipeline</em>
            </>
          }
          actions={
            <LwButton onClick={handleAddLoanOfficer}>
              <UserPlus size={16} />
              Add Candidate
            </LwButton>
          }
        />

        {/* Pipeline funnel — clickable stage steps wired to the status filter */}
        <RecruitmentPipelineFunnel
          candidates={recruits}
          activeStatus={filters.status}
          onSelectStatus={(status) => setFilters((f) => ({ ...f, status }))}
        />

        {/* Filters */}
        <RecruitmentFilters filters={filters} onFilterChange={setFilters} onExport={handleExport} />

        {/* Table */}
        <RecruitmentTable
          candidates={filteredCandidates}
          onEdit={handleEditCandidate}
          onTerminate={handleTerminateCandidate}
        />
      </div>

      {/* Recruitment Drawer */}
      <RecruitmentDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerOpenChange}
        candidate={selectedCandidate}
        onSave={handleSaveCandidate}
        initialTab={initialDrawerTab}
        autoOpenReviewModal={autoOpenReviewModal}
      />

      {/* Termination Dialog */}
      {candidateToTerminate && (
        <TerminationDialog
          open={terminationDialogOpen}
          onOpenChange={setTerminationDialogOpen}
          recruit={{
            id: candidateToTerminate.id,
            firstName: candidateToTerminate.firstName,
            lastName: candidateToTerminate.lastName,
            gmailAccount: candidateToTerminate.gmailAccount || null
          }}
          onTerminationComplete={handleTerminationComplete}
        />
      )}
    </LwThemeScope>
  );
};
